import { useMemo, useState } from 'react';
import {
  useGetRubrics,
  useSignOffRubric,
  getGetRubricsQueryKey,
  type ClassificationRubric,
} from '@edison/api-client-react';
import { ShieldCheck, FileCheck2, FileClock, FileX2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { AlertDialog } from '@/components/ui/alert-dialog.js';

function StatusBadge({ status }: { status: ClassificationRubric['status'] }) {
  if (status === 'active') {
    return (
      <Badge variant="positive">
        <FileCheck2 className="h-3 w-3" aria-hidden="true" />
        Active
      </Badge>
    );
  }
  if (status === 'draft') {
    return (
      <Badge variant="warning">
        <FileClock className="h-3 w-3" aria-hidden="true" />
        Draft — awaiting sign-off
      </Badge>
    );
  }
  return (
    <Badge variant="default">
      <FileX2 className="h-3 w-3" aria-hidden="true" />
      Superseded
    </Badge>
  );
}

function extractErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message;
  return message ?? fallback;
}

/**
 * Classification rubric panel — the versioned rubric text that commit classification (Stage 1
 * apportionment) runs against. "The active rubric" is the single status='active' row per tenant.
 * A pending DRAFT rubric (created via POST /api/rubrics — Iain seeds rubric v1 out of band, see
 * Task 4 Step 6) takes display priority over the currently active one, since it's the thing
 * requiring the tenant_admin's attention: sign-off.
 */
export function RubricPanel() {
  const { user } = useAuthStore();
  const canSignOff = user?.role === 'tenant_admin';

  const { data, isLoading, error, refetch } = useGetRubrics(undefined, {
    query: { queryKey: getGetRubricsQueryKey() },
  });
  const { mutate: signOffRubric, isPending: isSigningOff } = useSignOffRubric();

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [signOffError, setSignOffError] = useState<string | null>(null);

  const items = useMemo(() => data?.data?.items ?? [], [data]);
  const draft = useMemo(
    () => [...items].filter((r) => r.status === 'draft').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    [items],
  );
  const active = useMemo(() => items.find((r) => r.status === 'active'), [items]);
  const shown = draft ?? active;

  const handleSignOff = () => {
    if (!confirmingId) return;
    setSignOffError(null);
    signOffRubric(
      { id: confirmingId },
      {
        onSuccess: () => {
          setConfirmingId(null);
          void refetch();
        },
        onError: (err) => setSignOffError(extractErrorMessage(err, 'Failed to sign off rubric.')),
      },
    );
  };

  if (isLoading) {
    return <div className="h-32 w-full rounded-lg skeleton" />;
  }

  if (error) {
    return (
      <section className="rounded-lg border p-5 space-y-3">
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          Failed to load the classification rubric.
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Retry
        </Button>
      </section>
    );
  }

  return (
    <section className="rounded-lg border p-5 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="font-medium text-sm">Classification rubric</h2>
      </div>

      {!shown && <p className="text-sm text-muted-foreground text-center py-6">No rubric yet.</p>}

      {shown && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{shown.version}</span>
            <StatusBadge status={shown.status} />
          </div>

          <div className="rounded-lg border bg-muted/30 p-3 max-h-96 overflow-y-auto">
            <p className="text-sm whitespace-pre-wrap">{shown.body}</p>
          </div>

          {shown.citedArtefactIds.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Cited evidence</p>
              <ul className="space-y-1">
                {shown.citedArtefactIds.map((id) => (
                  <li key={id}>
                    <a href={`/artefacts/${id}`} className="text-xs text-primary hover:underline font-mono">
                      {id}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {shown.status === 'active' && shown.signedOffAt && (
            <p className="text-xs text-muted-foreground">
              Signed off {new Date(shown.signedOffAt).toLocaleString('en-AU')}
            </p>
          )}

          {shown.status === 'draft' && canSignOff && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setSignOffError(null);
                setConfirmingId(shown.id);
              }}
            >
              Sign off rubric {shown.version}
            </Button>
          )}
          {shown.status === 'draft' && !canSignOff && (
            <p className="text-xs text-muted-foreground">Only a tenant admin can sign off this rubric.</p>
          )}
          {signOffError && <p className="text-xs text-destructive">{signOffError}</p>}
        </div>
      )}

      <AlertDialog
        open={confirmingId !== null}
        title={`Sign off rubric ${shown?.version ?? ''}`}
        confirmLabel={isSigningOff ? 'Signing off…' : 'Sign off'}
        busy={isSigningOff}
        onConfirm={handleSignOff}
        onCancel={() => setConfirmingId(null)}
      >
        <p>
          Sign off rubric {shown?.version} — classification will run under this text; changes require a new
          version.
        </p>
      </AlertDialog>
    </section>
  );
}
